import type {
  StaffResponse,
  TreatmentPlanTemplateResponse,
  YearPlanResponse
} from '../../api/generated';
import type { ChangeSummary } from '../../hooks/useYearPlan';
import type { SimulationUnavailableReason } from '../../services/simulationAvailabilityService';
import type { WeekCalculationStatuses } from '../../hooks/useWeekCalculationStatuses';
import {
  buildGanttGroups,
  collectEditableSeries,
  collectGroupTreatmentPlanIds,
  computePerWeekColumnWidths
} from '../../utils/ganttUtils';
import { useGanttScroll } from '../../hooks/useGanttScroll';
import ChevronIcon from '../../assets/icons/chevron.svg?react';
import RefreshIcon from '../../assets/icons/refresh.svg?react';
import { Button } from '../UI/base/Button';
import { useHasPermission } from '../../hooks/useHasPermission';
import { PERMISSIONS } from '../../constants/permissions';
import { GanttWeekHeaderCorner, GanttWeekHeaderCells } from './GanttWeekHeader';
import { GanttGroupRow } from './GanttGroupRow';
import { GanttGroupLabel } from './GanttGroupLabel';
import { GanttEditToolbar } from './GanttEditToolbar';
import { PatientWarningModal } from './PatientWarningModal';
import { RemoveTreatmentPlansModal } from './RemoveTreatmentPlansModal';
import { useTreatmentPlanRemoval } from '../../hooks/useTreatmentPlanRemoval';
import { useAppSelector } from '../../store/hooks';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ContextMenu } from '../ContextMenu/ContextMenu';
import styles from './GanttChart.module.css';

import type { MoveHoverParams } from './GanttCell';
import type { RowMoveDirection } from '../../services/treatmentPlanRowOrderService';
import type { BedContext } from './WeekKpiCell/computeWeekKpi';
import { getCurrentIsoWeek } from '../../utils/dateUtils';

interface GanttChartProps {
  yearPlan: YearPlanResponse;
  year: number;
  changeSummary?: ChangeSummary | null;
  treatmentPlanTemplates: TreatmentPlanTemplateResponse[];
  isLocked?: boolean;
  lockedByCurrentUser?: boolean;
  lockedByName?: string | null;
  onAcquireLock?: () => Promise<boolean>;
  onGroupClick?: (treatmentPlanId: string, groupId: string) => void;
  onAddTreatmentPlan?: (template: TreatmentPlanTemplateResponse) => void;
  onMoveIntervention?: (params: MoveHoverParams) => void;
  onMoveStart?: () => void;
  onMoveDiscard?: () => void;
  onMoveAccept?: () => void;
  onDiscardAll?: () => void | Promise<void>;
  onAcceptAll?: () => void | Promise<void>;
  // Opens the simulation dialog; offered in edit mode only, beside Accept plan.
  // While a reason is set the button is disabled and shows that reason.
  onSimulate?: () => void;
  simulationUnavailableReason?: SimulationUnavailableReason | null;
  onAddTreatmentPlanBatch?: (
    treatmentPlanTemplateId: string,
    weekNumber: number,
    mode: 'single' | 'series'
  ) => void;
  onDeleteTreatmentPlan?: (treatmentPlanId: string) => void;
  // Required: the row menu is useless without them, and optional props would
  // turn a missing wiring into a rename that silently does nothing.
  onDeleteTreatmentPlans: (treatmentPlanIds: string[]) => void;
  onRenameTreatmentPlans: (treatmentPlanIds: string[], name: string) => void;
  onMoveTreatmentPlanRow: (templateId: string, direction: RowMoveDirection) => void;
  onAddPatient?: (treatmentPlanId: string, groupId: string) => void;
  onWeekStatusClick?: (weekNumber: number) => void;
  onOpenCalendar?: (weekNumber: number) => void;
  onRetryCalculationStatuses?: () => void;
  // Opens the dialog for calculating a range of weeks in one go.
  onCalculateWeeks?: () => void;
  weekCalculations?: WeekCalculationStatuses;
  // What the solver could not place, per week; feeds the header's unplanned tag.
  onOpenUnscheduled?: (weekNumber: number) => void;
  // Settled staff roster for the location; undefined until loaded. Feeds the
  // week KPI's capacity thresholds (see GanttWeekHeaderCells).
  staff?: readonly StaffResponse[];
  // What the bed chip is judged against (see GanttWeekHeaderCells). Undefined
  // until the room list has loaded.
  beds?: BedContext;
}

export const GanttChart = ({
  yearPlan,
  year,
  changeSummary,
  treatmentPlanTemplates,
  isLocked,
  lockedByCurrentUser,
  lockedByName,
  onAcquireLock,
  onGroupClick,
  onAddTreatmentPlan,
  onMoveIntervention,
  onMoveStart,
  onMoveDiscard,
  onMoveAccept,
  onDiscardAll,
  onAcceptAll,
  onSimulate,
  simulationUnavailableReason,
  onAddTreatmentPlanBatch,
  onDeleteTreatmentPlan,
  onDeleteTreatmentPlans,
  onRenameTreatmentPlans,
  onMoveTreatmentPlanRow,
  onAddPatient,
  onWeekStatusClick,
  onOpenCalendar,
  onRetryCalculationStatuses,
  onCalculateWeeks,
  weekCalculations,
  onOpenUnscheduled,
  staff,
  beds
}: GanttChartProps) => {
  const { t } = useTranslation();
  // Same gate as the per-week status chips: queueing a solve is ALGORITHM_JOBS_EXECUTE.
  const canExecuteCalculation = useHasPermission(PERMISSIONS.AlgorithmJobsExecute);

  const isEditMode = useAppSelector((state) => state.applicationSlice.isEditMode);
  const totalWeeks = yearPlan.weeks.length;
  // Plans with no weeks are edit-time placeholders (template added, nothing
  // rolled out yet). Their row only exists while editing; outside edit mode
  // they must not render — including legacy empty plans still on the server.
  const visiblePlans = useMemo(
    () =>
      isEditMode
        ? yearPlan.treatmentPlans
        : yearPlan.treatmentPlans.filter((p) => p.weeks.length > 0),
    [yearPlan.treatmentPlans, isEditMode]
  );
  const groups = useMemo(
    () => buildGanttGroups({ ...yearPlan, treatmentPlans: visiblePlans }),
    [yearPlan, visiblePlans]
  );
  // Row order is the order of this year's plans, so a row holding only
  // carried-over plans has no position to move; buildGanttGroups puts those last.
  const movableRowCount = groups.filter((g) => collectEditableSeries(g).length > 0).length;
  const weekWidths = useMemo(
    () => computePerWeekColumnWidths(groups, totalWeeks),
    [groups, totalWeeks]
  );
  const weekGridTemplateColumns = weekWidths.map((w) => `${String(w)}px`).join(' ');
  const {
    containerRef,
    headerViewportRef,
    headerTrackRef,
    scrollLeft,
    scrollRight,
    scrollToWeek,
    scrollToStart
  } = useGanttScroll();

  const lastScrolledYearRef = useRef<number | null>(null);

  useEffect(() => {
    if (lastScrolledYearRef.current === year) return;
    lastScrolledYearRef.current = year;

    if (year === new Date().getFullYear()) {
      scrollToWeek(getCurrentIsoWeek() - 1, weekWidths);
    } else {
      scrollToStart();
    }
  }, [year, weekWidths, scrollToWeek, scrollToStart]);

  const availableTemplates = useMemo(() => {
    const usedTemplateIds = new Set(
      yearPlan.treatmentPlans.map((tp) => tp.treatmentPlanTemplateId)
    );
    return treatmentPlanTemplates.filter((t) => t.id && !usedTemplateIds.has(t.id));
  }, [yearPlan.treatmentPlans, treatmentPlanTemplates]);

  const [menuAnchorRect, setMenuAnchorRect] = useState<DOMRect | null>(null);

  // One removal flow for the whole chart. Both entry points — a plan's own menu
  // and the row label's menu — raise the same dialog from the same pending
  // state, so neither can grow a quiet path around the warning.
  const {
    prompt: removalPrompt,
    patientsByPlanId,
    requestPlanRemoval,
    requestRowRemoval,
    confirmScopedRemoval,
    confirmSingleRemoval,
    cancelRemoval
  } = useTreatmentPlanRemoval({
    groups,
    weeks: yearPlan.weeks,
    onDeleteTreatmentPlan,
    onDeleteTreatmentPlans
  });

  const handleAddClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorRect(e.currentTarget.getBoundingClientRect());
  }, []);

  const menuItems = useMemo(
    () =>
      availableTemplates.map((tp) => ({
        label: tp.name,
        onClick: () => {
          onAddTreatmentPlan?.(tp);
          setMenuAnchorRect(null);
        }
      })),
    [availableTemplates, onAddTreatmentPlan]
  );

  return (
    <div className={`${styles.wrapper} ${isEditMode ? styles.wrapperEditMode : ''}`}>
      <div className={styles.toolbar}>
        <GanttEditToolbar
          isLocked={isLocked}
          lockedByCurrentUser={lockedByCurrentUser}
          lockedByName={lockedByName}
          changeSummary={changeSummary}
          onAcquireLock={onAcquireLock}
          onDiscardAll={onDiscardAll}
          onAcceptAll={onAcceptAll}
          onSimulate={onSimulate}
          simulationUnavailableReason={simulationUnavailableReason}
        />
        <div className={styles.toolbarEnd}>
          {canExecuteCalculation && onCalculateWeeks && (
            <Button
              type="secondary"
              icon={<RefreshIcon className={styles.calculateWeeksIcon} />}
              onClick={onCalculateWeeks}
              classes={styles.calculateWeeksButton}
            >
              {t('gantt-calculate-weeks-button')}
            </Button>
          )}
          <div className={styles.scrollButtons}>
            <button
              type="button"
              className={styles.scrollButton}
              onClick={scrollLeft}
              aria-label={t('gantt-scroll-left-aria')}
            >
              <ChevronIcon className={styles.chevronLeft} />
            </button>
            <button
              type="button"
              className={styles.scrollButton}
              onClick={scrollRight}
              aria-label={t('gantt-scroll-right-aria')}
            >
              <ChevronIcon className={styles.chevronRight} />
            </button>
          </div>
        </div>
      </div>
      <div className={styles.container} tabIndex={0}>
        <div className={styles.ganttLayout}>
          {/* The corner has no button of its own, so it needs telling that the
              cells grew by a row — the two must stay one visual band. */}
          <GanttWeekHeaderCorner hasCalendarRow={onOpenCalendar !== undefined} />
          {/* The week headers live here rather than inside the horizontal
              scroller: that scroller would be their nearest scroll container,
              which no sticky offset can escape, so they would scroll away from
              the pinned corner. The track is moved sideways by useGanttScroll
              instead. */}
          {/* Pannable by drag and by touch, like the rows below: the strip used
              to sit inside the scroller, so grabbing it panned the year, and it
              still covers the rows it was lifted off. useGanttScroll attaches
              that to this ref rather than taking handler props here — see the
              comment on its listener effect for why a plain div is the honest
              markup for a pannable box. */}
          <div ref={headerViewportRef} className={styles.headerViewport}>
            <div
              ref={headerTrackRef}
              className={styles.headerTrack}
              style={{ gridTemplateColumns: weekGridTemplateColumns }}
            >
              <GanttWeekHeaderCells
                yearPlan={yearPlan}
                onWeekStatusClick={onWeekStatusClick}
                onOpenCalendar={onOpenCalendar}
                onRetryCalculationStatuses={onRetryCalculationStatuses}
                weekCalculations={weekCalculations}
                onOpenUnscheduled={onOpenUnscheduled}
                staff={staff}
                beds={beds}
              />
            </div>
          </div>
          {groups.map((group, index) => (
            <GanttGroupLabel
              // Keyed on edit mode as well as the row: leaving edit mode
              // releases the lock and takes the toolbar away, so an open rename
              // has to go with it rather than live on with no way to be saved
              // or discarded. Remounting drops that state without an effect.
              key={`${group.templateId}-${String(isEditMode)}`}
              group={group}
              isEditMode={isEditMode}
              onRenameRow={(renamedGroup, name) => {
                onRenameTreatmentPlans(collectGroupTreatmentPlanIds(renamedGroup), name);
              }}
              onRemoveRow={requestRowRemoval}
              canMoveUp={index > 0 && index < movableRowCount}
              canMoveDown={index < movableRowCount - 1}
              onMoveRow={(movedGroup, direction) => {
                onMoveTreatmentPlanRow(movedGroup.templateId, direction);
              }}
            />
          ))}
          <div
            ref={containerRef}
            className={styles.scrollColumn}
            style={{
              gridTemplateColumns: weekGridTemplateColumns,
              gridRow: `2 / span ${groups.length + 1}`
            }}
          >
            {groups.map((group) => (
              <GanttGroupRow
                // Keyed on edit mode as well as the row, the same as the label
                // above: leaving edit mode releases the lock, so an in-flight
                // move and the acknowledged-warning set must go with it rather
                // than wait behind a chart that can no longer be edited.
                // Remounting drops them without an effect. The pending removal
                // is not among them any more — it belongs to the chart now, and
                // useTreatmentPlanRemoval drops it on the same signal.
                key={`${group.templateId}-${String(isEditMode)}`}
                group={group}
                totalWeeks={totalWeeks}
                weeks={yearPlan.weeks}
                isEditMode={isEditMode}
                onGroupClick={onGroupClick}
                onMoveIntervention={onMoveIntervention}
                onMoveStart={onMoveStart}
                onMoveDiscard={onMoveDiscard}
                onMoveAccept={onMoveAccept}
                onAddTreatmentPlanBatch={onAddTreatmentPlanBatch}
                onRequestRemoval={(treatmentPlanId) => {
                  requestPlanRemoval(group, treatmentPlanId);
                }}
                patientsByPlanId={patientsByPlanId}
                onAddPatient={onAddPatient}
              />
            ))}
          </div>
          <div className={styles.bottomSpacer} />
        </div>
      </div>
      <button
        type="button"
        className={`${styles.addTreatmentTypeButton} ${isEditMode ? styles.addTreatmentTypeButtonVisible : ''}`}
        onClick={handleAddClick}
        tabIndex={isEditMode ? 0 : -1}
        disabled={availableTemplates.length === 0}
        data-tooltip={availableTemplates.length === 0 ? t('all-treatment-types-added') : undefined}
      >
        {t('add-treatment-type')}
      </button>
      {menuAnchorRect && (
        <ContextMenu
          anchorRect={menuAnchorRect}
          items={menuItems}
          onClose={() => setMenuAnchorRect(null)}
        />
      )}
      {removalPrompt?.kind === 'scope' && (
        <RemoveTreatmentPlansModal
          group={removalPrompt.group}
          anchor={removalPrompt.anchor}
          lockedScope={removalPrompt.lockedScope}
          weeks={yearPlan.weeks}
          patientsByPlanId={patientsByPlanId}
          onClose={cancelRemoval}
          onConfirm={confirmScopedRemoval}
        />
      )}
      {removalPrompt?.kind === 'confirm' && (
        <PatientWarningModal
          title={t('delete-warning-title')}
          message={t('delete-warning-message')}
          patientsLabel={t('delete-warning-patients-label')}
          confirmLabel={t('delete-warning-confirm')}
          cancelLabel={t('delete-warning-cancel')}
          patientNames={removalPrompt.patients.map((p) => p.name || t('unknown-patient'))}
          onClose={cancelRemoval}
          onConfirm={confirmSingleRemoval}
        />
      )}
    </div>
  );
};
